/**
 * AES Encryption Service
 *
 * Pure cryptography utilities for encrypting and decrypting data
 * compatible with backend API protocol.
 *
 * @module aes.service
 */

/**
 * @typedef {Object} EncryptedPayload
 * @property {string} data - Base64-encoded encrypted data
 * @property {string} iv - Base64-encoded initialization vector
 */

/**
 * @typedef {Object} EncryptionConfig
 * @property {string} algorithm - AES mode (default: AES-CBC)
 * @property {number} ivLength - IV length in bytes (12 for GCM, 16 for CBC)
 */

/** Default configuration using AES-CBC */
const DEFAULT_CONFIG = {
  algorithm: "AES-CBC",
  ivLength: 16,
};

/**
 * Converts a base64 string to Uint8Array
 * @param {string} base64 - Base64 encoded string
 * @returns {Uint8Array} Decoded byte array
 */
function base64ToUint8Array(base64) {
  try {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } catch (error) {
    throw new Error(`Invalid base64 string: ${error.message}`);
  }
}

/**
 * Converts Uint8Array to base64 string
 * @param {Uint8Array} buffer - Byte array to encode
 * @returns {string} Base64 encoded string
 */
function uint8ArrayToBase64(buffer) {
  let binaryString = "";
  for (let i = 0; i < buffer.length; i++) {
    binaryString += String.fromCharCode(buffer[i]);
  }
  return btoa(binaryString);
}

/**
 * Generates a cryptographically strong random IV
 * @param {number} length - IV length in bytes
 * @returns {Uint8Array} Random IV as Uint8Array
 */
function generateIV(length) {
  return crypto.getRandomValues(new Uint8Array(length));
}

/**
 * Imports a base64-encoded AES key for use with SubtleCrypto
 * @param {string} base64Key - Base64 encoded encryption key
 * @param {string} algorithm - AES algorithm to use
 * @returns {Promise<CryptoKey>} CryptoKey for encryption/decryption
 */
async function importKey(base64Key, algorithm) {
  try {
    const keyData = base64ToUint8Array(base64Key);
    return await crypto.subtle.importKey(
      "raw",
      keyData.buffer,
      { name: algorithm },
      false,
      ["encrypt", "decrypt"],
    );
  } catch (error) {
    throw new Error(`Failed to import encryption key: ${error.message}`);
  }
}

/**
 * Encrypts data using AES encryption
 *
 * @param {*} data - Data to encrypt (will be JSON serialized)
 * @param {string} base64Key - Base64 encoded encryption key
 * @param {EncryptionConfig} [config=DEFAULT_CONFIG] - Optional encryption configuration
 * @returns {Promise<EncryptedPayload>} Encrypted payload with data and IV
 *
 * @throws {EncryptionError} If encryption fails
 *
 * @example
 * ```javascript
 * const payload = await encrypt({ userId: 123 }, 'base64Key==');
 * // Returns: { data: 'encrypted...', iv: 'random...' }
 * ```
 */
export async function encrypt(data, base64Key, config = DEFAULT_CONFIG) {
  try {
    // Validate inputs
    if (!data) {
      throw new Error("Data to encrypt cannot be empty");
    }
    if (!base64Key) {
      throw new Error("Encryption key is required");
    }

    // Serialize data to JSON string
    const jsonString = JSON.stringify(data);
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(jsonString);

    // Import the encryption key
    const cryptoKey = await importKey(base64Key, config.algorithm);

    // Generate random IV
    const iv = generateIV(config.ivLength);

    // Encrypt the data
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: config.algorithm,
        iv: new Uint8Array(iv),
      },
      cryptoKey,
      dataBuffer,
    );

    // Convert to base64
    const encryptedData = uint8ArrayToBase64(new Uint8Array(encryptedBuffer));
    const ivBase64 = uint8ArrayToBase64(iv);

    return {
      data: encryptedData,
      iv: ivBase64,
    };
  } catch (error) {
    throw new Error(`Encryption failed: ${error.message}`);
  }
}

/**
 * Decrypts an encrypted payload
 *
 * @param {EncryptedPayload} payload - Encrypted payload with data and IV
 * @param {string} base64Key - Base64 encoded encryption key
 * @param {EncryptionConfig} [config=DEFAULT_CONFIG] - Optional encryption configuration
 * @returns {Promise<*>} Decrypted and parsed data
 *
 * @throws {DecryptionError} If decryption fails
 *
 * @example
 * ```javascript
 * const decrypted = await decrypt(
 *   { data: 'encrypted...', iv: 'random...' },
 *   'base64Key=='
 * );
 * // Returns: { userId: 123 }
 * ```
 */
export async function decrypt(payload, base64Key, config = DEFAULT_CONFIG) {
  try {
    // Validate inputs
    if (!payload || !payload.data || !payload.iv) {
      throw new Error("Invalid encrypted payload");
    }
    if (!base64Key) {
      throw new Error("Decryption key is required");
    }

    // Import the decryption key
    const cryptoKey = await importKey(base64Key, config.algorithm);

    // Convert base64 to buffers
    const encryptedData = base64ToUint8Array(payload.data);
    const iv = base64ToUint8Array(payload.iv);

    // Decrypt the data
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: config.algorithm,
        iv: iv,
      },
      cryptoKey,
      encryptedData,
    );

    // Convert buffer to string and parse JSON
    const decoder = new TextDecoder();
    const jsonString = decoder.decode(decryptedBuffer);
    return JSON.parse(jsonString);
  } catch (error) {
    throw new Error(`Decryption failed: ${error.message}`);
  }
}

/**
 * Creates an encryption service instance with a pre-configured key
 * Useful for dependency injection or when the same key is used throughout the app
 *
 * @param {string} base64Key - Base64 encoded encryption key
 * @param {EncryptionConfig} [config=DEFAULT_CONFIG] - Optional encryption configuration
 * @returns {Object} Object with encrypt and decrypt methods
 *
 * @example
 * ```javascript
 * const cryptoService = createEncryptionService(import.meta.env.VITE_AES_KEY);
 *
 * const encrypted = await cryptoService.encrypt({ userId: 123 });
 * const decrypted = await cryptoService.decrypt(encrypted);
 * ```
 */
export function createEncryptionService(base64Key, config = DEFAULT_CONFIG) {
  return {
    /**
     * Encrypts data using the pre-configured key
     * @param {*} data - Data to encrypt
     * @returns {Promise<EncryptedPayload>} Encrypted payload
     */
    encrypt: (data) => encrypt(data, base64Key, config),

    /**
     * Decrypts data using the pre-configured key
     * @param {EncryptedPayload} payload - Encrypted payload
     * @returns {Promise<*>} Decrypted data
     */
    decrypt: (payload) => decrypt(payload, base64Key, config),
  };
}
